// Web device identity + registration. Mirrors the mobile app's
// `src/apis/device` module API (same `getDeviceId` name) so logic ports
// cleanly, but backs onto localStorage instead of expo-secure-store —
// localStorage is synchronous, so `getDeviceId` is sync here.
//
// Web rows never carry a push token: the server forces sentinel values
// (bundleId/projectId/appVariant "web") and computes isActive=false, so the
// push send path skips them. Registration exists purely for device tracking
// (lastSeenAt / "where am I signed in").

import { instance } from "../index";
import { getToken } from "../storage";

// Per-install stable device id, the web analogue of the mobile app's
// SecureStore-backed "tarbaaDeviceId".
const DEVICE_ID_KEY = "tarbaaWebDeviceId";

// Day-bucket throttle: at most one register call per user per day (mirrors
// the mobile app's day-bucketed registration cache — keeps the server's
// Device.lastSeenAt fresh without hammering the endpoint on every page load).
const REG_KEY_PREFIX = "webDeviceReg_";
const REG_KEY = (userId) => `${REG_KEY_PREFIX}${userId}`;

// UUID v4 without crypto.randomUUID, for non-secure contexts (plain-http LAN
// testing) where it's unavailable. Math.random is fine — the id only needs to
// be unique per install, not unguessable.
const fallbackUUID = () =>
  "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });

const makeUUID = () =>
  typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : fallbackUUID();

// In-memory fallback so the id at least stays stable for the tab's lifetime
// when localStorage is unavailable (private-mode edge cases).
let memoryDeviceId = null;

export const getDeviceId = () => {
  try {
    let id = localStorage.getItem(DEVICE_ID_KEY);
    if (!id) {
      id = makeUUID();
      localStorage.setItem(DEVICE_ID_KEY, id);
    }
    return id;
  } catch (e) {
    if (!memoryDeviceId) memoryDeviceId = makeUUID();
    return memoryDeviceId;
  }
};

const todayBucket = () => new Date().toISOString().slice(0, 10);

// Upsert this browser as a device row for the logged-in user. Fire-and-forget:
// callers never await this meaningfully, and every failure is swallowed —
// device tracking must never break an auth flow.
export const registerWebDevice = async (userId) => {
  if (!userId) return;
  try {
    const regKey = REG_KEY(userId);
    const dayBucket = todayBucket();
    try {
      if (localStorage.getItem(regKey) === dayBucket) return;
    } catch (e) {
      // storage unavailable — register anyway, just without the throttle
    }

    await instance.put("/api/notification/devices", {
      deviceId: getDeviceId(),
      expoPushToken: null,
      platform: "web",
      appVariant: "web",
      bundleId: "web",
      appVersion: process.env.REACT_APP_VERSION || "web",
      buildNumber: null,
      projectId: "web",
      permissionStatus: "undetermined",
      isActive: false,
    });

    try {
      localStorage.setItem(regKey, dayBucket);
    } catch (e) {
      // noop
    }
  } catch (error) {
    console.debug(
      "Failed to register web device:",
      error.response?.data || error.message
    );
  }
};

// Mark this browser's device row inactive on the backend. Called at the start
// of logout, BEFORE the token is deleted. Fire-and-forget like register.
export const deactivateWebDevice = async () => {
  try {
    // Snapshot the token NOW: logout deletes it synchronously right after
    // this call returns, but axios request interceptors run in a microtask —
    // by then getToken() would already be null. The interceptor only sets
    // Authorization when a token exists, so this explicit header survives.
    const token = getToken();
    await instance.post(
      "/api/notification/devices/deactivate",
      { deviceId: getDeviceId() },
      token ? { headers: { Authorization: `Bearer ${token}` } } : undefined
    );
  } catch (error) {
    console.debug(
      "Failed to deactivate web device:",
      error.response?.data || error.message
    );
  } finally {
    // Clear every user's day-bucket stamp so the next login re-registers
    // immediately (the server row was just marked inactive). Unlike mobile's
    // SecureStore, localStorage keys are enumerable — no deactivate stamp
    // needed.
    try {
      Object.keys(localStorage)
        .filter((key) => key.startsWith(REG_KEY_PREFIX))
        .forEach((key) => localStorage.removeItem(key));
    } catch (e) {
      // noop
    }
  }
};
